import { useState } from 'react';
import { Button, Dialog, FormControlLabel, Radio, RadioGroup } from '@cere/cere-design-system';
import type { Scope } from '@cef-ai/vault-sdk';
import type { MarketplaceAgentManifest } from '@/src/marketplace/types';

interface Props {
  open: boolean;
  agent: MarketplaceAgentManifest;
  scopes: Scope[];
  onConnect: (scope: string) => Promise<void>;
  onClose: () => void;
}

function initialScope(scopes: Scope[]): string {
  return scopes.find((s) => s.name === 'default')?.name ?? scopes[0]?.name ?? 'default';
}

export function InstallAgentDialog({ open, agent, scopes, onConnect, onClose }: Props) {
  const [selected, setSelected] = useState(initialScope(scopes));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConnect() {
    setSubmitting(true);
    setError(null);
    try {
      await onConnect(selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      title={`Connect ${agent.card?.name ?? agent.agentId}`}
      onClose={() => !submitting && onClose()}
      isLoading={submitting}
      customActions={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={handleConnect} disabled={submitting}>
            {submitting ? 'Connecting...' : 'Connect'}
          </Button>
        </>
      }
    >
      <div className="install-body">
        {agent.card?.description && (
          <p className="install-desc">{agent.card.description}</p>
        )}
        <div className="install-eyebrow">Choose a scope</div>
        <RadioGroup className="install-scopes" value={selected} onChange={(_, v) => setSelected(v)}>
          {scopes.map((s) => (
            <FormControlLabel
              key={s.name}
              className="install-scope"
              value={s.name}
              control={<Radio />}
              label={s.displayName ?? s.name}
            />
          ))}
        </RadioGroup>
        {error && <div className="install-error">{error}</div>}
      </div>
    </Dialog>
  );
}
